import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Globe2, Crosshair, ShieldCheck, Wallet, Radio, MapPin } from 'lucide-react';

/**
 * Original cyberpunk "campaign operator" scene.
 * Stylized hooded silhouette inside a neon control room, surrounded by
 * holographic adtech dashboards (KPI cards, bar chart, GEO grid, source list).
 * No copyrighted characters — fully original SVG illustration.
 */
export default function CyberOperator() {
  return (
    <div className="relative w-full h-full min-h-[560px] lg:min-h-[640px]" data-testid="cyber-operator-scene">
      {/* Halo glow */}
      <div
        className="absolute inset-0"
        aria-hidden
        style={{
          background:
            'radial-gradient(420px 360px at 55% 45%, rgba(34,211,238,0.18), transparent 70%), radial-gradient(360px 320px at 70% 80%, rgba(124,58,237,0.18), transparent 70%)',
        }}
      />

      {/* Outer orbit ring */}
      <svg className="absolute inset-0 w-full h-full spin-slow opacity-25" viewBox="0 0 600 600" aria-hidden>
        <circle cx="300" cy="300" r="270" fill="none" stroke="#22D3EE" strokeOpacity="0.45" strokeDasharray="3 8" strokeWidth="1" />
        <circle cx="300" cy="300" r="210" fill="none" stroke="#7C3AED" strokeOpacity="0.35" strokeDasharray="2 6" strokeWidth="1" />
      </svg>

      {/* CHARACTER: hooded operator silhouette */}
      <motion.svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 480 640"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <defs>
          <linearGradient id="cy-body" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0E1A2E" />
            <stop offset="100%" stopColor="#050B17" />
          </linearGradient>
          <linearGradient id="cy-trim" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22D3EE" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
          <linearGradient id="cy-floor" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="rgba(34,211,238,0.0)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0.4)" />
          </linearGradient>
          <radialGradient id="cy-floor-pad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(34,211,238,0.45)" />
            <stop offset="60%" stopColor="rgba(34,211,238,0.10)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0)" />
          </radialGradient>
          <filter id="cy-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="cy-soft-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Floor light pad */}
        <ellipse cx="240" cy="588" rx="180" ry="22" fill="url(#cy-floor-pad)" />

        {/* Floor perspective grid */}
        <g opacity="0.45" stroke="url(#cy-floor)" strokeWidth="0.6">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <line key={`h-${i}`} x1={60 - i * 20} y1={520 + i * 14} x2={420 + i * 20} y2={520 + i * 14} />
          ))}
          {[-3, -2, -1, 0, 1, 2, 3].map((i) => (
            <line key={`v-${i}`} x1={240 + i * 28} y1="520" x2={240 + i * 80} y2="610" />
          ))}
        </g>

        {/* Holographic dashboard frame BEHIND operator */}
        <g transform="translate(120,120)" opacity="0.85">
          <rect x="0" y="0" width="240" height="170" rx="14" fill="rgba(34,211,238,0.05)" stroke="#22D3EE" strokeOpacity="0.55" strokeWidth="1" filter="url(#cy-glow)" />
          <rect x="12" y="14" width="50" height="6" rx="3" fill="#22D3EE" opacity="0.7" />
          <rect x="12" y="28" width="100" height="4" rx="2" fill="#FFFFFF" opacity="0.55" />
          <rect x="12" y="38" width="70" height="4" rx="2" fill="#FFFFFF" opacity="0.35" />
          {/* Mini chart bars */}
          {[28, 44, 36, 60, 50, 80, 62, 92, 76, 88].map((h, i) => (
            <rect key={i} x={14 + i * 22} y={150 - h} width="12" height={h} rx="2" fill="#22D3EE" opacity={0.55 + (i % 3) * 0.12} />
          ))}
          {/* Trend line */}
          <polyline
            points="14,110 36,98 58,102 80,82 102,86 124,68 146,72 168,52 190,58 212,42"
            fill="none"
            stroke="#7C3AED"
            strokeWidth="1.5"
            opacity="0.85"
          />
        </g>

        {/* === OPERATOR SILHOUETTE === */}
        {/* Long trench coat / body */}
        <path
          d="M 240 180
             C 215 188, 200 220, 198 260
             L 192 360
             C 188 420, 184 470, 180 520
             L 188 530
             C 198 540, 210 545, 228 545
             L 238 545
             L 240 480
             L 250 545
             L 268 545
             C 288 545, 302 540, 312 530
             L 320 520
             C 316 470, 312 420, 308 360
             L 302 260
             C 300 220, 285 188, 260 180
             Z"
          fill="url(#cy-body)"
          stroke="#22D3EE"
          strokeOpacity="0.5"
          strokeWidth="1.1"
        />

        {/* Spine glow line */}
        <line x1="250" y1="195" x2="250" y2="480" stroke="url(#cy-trim)" strokeWidth="1.5" opacity="0.85" filter="url(#cy-glow)" />
        {/* Sleeve trim */}
        <line x1="196" y1="330" x2="190" y2="480" stroke="#22D3EE" strokeWidth="1.2" opacity="0.8" filter="url(#cy-glow)" />
        <line x1="304" y1="330" x2="310" y2="480" stroke="#A78BFA" strokeWidth="1.2" opacity="0.8" filter="url(#cy-glow)" />

        {/* Hood */}
        <path
          d="M 230 178
             C 215 162, 210 138, 220 118
             C 230 100, 252 92, 274 100
             C 296 108, 304 130, 298 152
             C 294 168, 286 180, 278 188
             L 270 192
             L 230 192 Z"
          fill="#08111E"
          stroke="#22D3EE"
          strokeOpacity="0.55"
          strokeWidth="1.1"
        />
        {/* Hair tufts (stylized anime nod) */}
        <path d="M 234 110 L 248 80 L 252 108 Z" fill="#22D3EE" opacity="0.85" filter="url(#cy-glow)" />
        <path d="M 252 112 L 266 78 L 268 108 Z" fill="#A78BFA" opacity="0.85" filter="url(#cy-glow)" />
        <path d="M 268 114 L 282 92 L 282 116 Z" fill="#22D3EE" opacity="0.7" />

        {/* Visor: thin glowing band */}
        <rect x="228" y="148" width="68" height="7" rx="3.5" fill="#0B172A" stroke="#22D3EE" strokeOpacity="0.7" strokeWidth="0.8" />
        <rect x="232" y="150" width="60" height="3" rx="1.5" fill="#22D3EE" filter="url(#cy-soft-glow)" />

        {/* Headset arc */}
        <path d="M 222 142 C 222 116, 296 116, 302 142" fill="none" stroke="#A78BFA" strokeOpacity="0.7" strokeWidth="1.2" />
        <circle cx="222" cy="148" r="4" fill="#0B172A" stroke="#A78BFA" strokeOpacity="0.7" strokeWidth="1" />
        <circle cx="302" cy="148" r="4" fill="#0B172A" stroke="#A78BFA" strokeOpacity="0.7" strokeWidth="1" />

        {/* Collar accent */}
        <path d="M 222 192 L 240 210 L 258 210 L 278 192" fill="none" stroke="#22D3EE" strokeOpacity="0.6" strokeWidth="1" />
        <path d="M 232 220 L 240 230 L 258 230 L 268 220" fill="none" stroke="#22D3EE" strokeOpacity="0.4" strokeWidth="0.8" />

        {/* Right arm extended holding holo tablet */}
        <path
          d="M 296 280 C 318 275, 348 280, 372 290 C 376 300, 374 312, 366 318 C 350 322, 332 320, 312 312 Z"
          fill="#0B172A"
          stroke="#22D3EE"
          strokeOpacity="0.55"
          strokeWidth="1"
        />
        {/* Holo tablet panel in hand */}
        <g transform="translate(330,256)">
          <rect x="0" y="0" width="86" height="58" rx="6" fill="rgba(34,211,238,0.07)" stroke="#22D3EE" strokeOpacity="0.7" strokeWidth="0.8" filter="url(#cy-glow)" />
          <rect x="6" y="6" width="36" height="4" rx="2" fill="#22D3EE" opacity="0.85" />
          <rect x="6" y="14" width="60" height="3" rx="1.5" fill="#FFFFFF" opacity="0.4" />
          <polyline points="6,46 16,38 26,42 36,30 46,34 56,22 66,26 76,16" fill="none" stroke="#A78BFA" strokeWidth="1.2" />
          <circle cx="76" cy="16" r="2" fill="#A78BFA" />
        </g>

        {/* Pulse rings under feet */}
        <ellipse cx="240" cy="560" rx="68" ry="6" fill="none" stroke="#22D3EE" strokeOpacity="0.45" strokeWidth="0.8" />
        <ellipse cx="240" cy="568" rx="96" ry="7" fill="none" stroke="#7C3AED" strokeOpacity="0.32" strokeWidth="0.7" />

        {/* Floating particle dots */}
        {[
          [80, 200], [110, 380], [400, 200], [430, 400], [70, 460], [410, 470], [150, 90], [350, 90],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.6" fill="#22D3EE" className="pulse-glow" style={{ animationDelay: `${(i * 0.5) % 4}s` }} />
        ))}

        {/* Faint network lines */}
        <g opacity="0.35" stroke="#22D3EE" strokeWidth="0.6" fill="none">
          <path d="M 80 200 Q 160 240 240 220" />
          <path d="M 400 200 Q 320 240 240 220" />
          <path d="M 110 380 Q 200 360 240 380" />
          <path d="M 430 400 Q 340 380 290 390" />
        </g>
      </motion.svg>

      {/* Floating chip: Live Traffic Flow */}
      <FloatingChip icon={Radio} label="Live Traffic Flow" sub="streaming" tone="emerald" className="left-[2%] top-[10%] float-y-slow" delay={0.2} />
      {/* Floating chip: 150+ GEOs */}
      <FloatingChip icon={Globe2} label="150+ GEOs" sub="global routing" tone="cyan" className="right-[2%] top-[18%] float-y" delay={0.35} />
      {/* Floating chip: Source Quality */}
      <FloatingChip icon={Crosshair} label="Source Quality" sub="94.6 score" tone="violet" className="left-[0%] top-[48%] float-y-fast" delay={0.5} />
      {/* Floating chip: Smart Routing */}
      <FloatingChip icon={MapPin} label="Smart Routing" sub="format · GEO · device" tone="cyan" className="right-[0%] top-[52%] float-y-slow" delay={0.65} />
      {/* Floating chip: Budget Top-Up */}
      <FloatingChip icon={Wallet} label="Budget Top-Up" sub="$8,250 funded" tone="violet" className="left-[6%] bottom-[8%] float-y" delay={0.8} />
      {/* Floating chip: Fraud Signals */}
      <FloatingChip icon={ShieldCheck} label="Fraud Signals Checked" sub="continuous" tone="emerald" className="right-[4%] bottom-[6%] float-y-fast" delay={0.95} />

      {/* Tiny live indicator at top */}
      <div className="absolute top-[0%] left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B172A]/80 border border-white/10 backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.4)]">
        <Activity className="h-3 w-3 text-emerald-400" />
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-slate-300">control room</span>
      </div>
    </div>
  );
}

function FloatingChip({ icon: Icon, label, sub, className = '', tone = 'cyan', delay = 0 }) {
  const tones = {
    cyan: { ring: 'border-[#22D3EE]/30', text: 'text-[#22D3EE]', glow: 'rgba(34,211,238,0.35)' },
    emerald: { ring: 'border-emerald-400/30', text: 'text-emerald-400', glow: 'rgba(16,185,129,0.35)' },
    violet: { ring: 'border-[#A78BFA]/35', text: 'text-[#A78BFA]', glow: 'rgba(167,139,250,0.35)' },
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className={`absolute z-10 rounded-2xl bg-[#0B172A]/85 border border-white/10 ${tones.ring} backdrop-blur-xl px-3 py-2.5 shadow-[0_18px_40px_rgba(0,0,0,0.45)] ${className}`}
      style={{ boxShadow: `0 14px 36px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04), 0 0 22px ${tones.glow}` }}
    >
      <div className="flex items-center gap-2.5">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 border border-white/10 ${tones.text}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="text-left">
          <div className="font-display text-[12.5px] font-semibold text-white leading-none tracking-[-0.01em]">{label}</div>
          <div className="text-[10px] text-slate-400 mt-1">{sub}</div>
        </div>
      </div>
    </motion.div>
  );
}
